// External Imports
import { Fragment, useContext, useEffect, useState } from "react";
import MakesAndModals from "../../assets/data/makesAndModels.json";
import Colors from "../../assets/data/colors.json";
import { useSearchParams } from "react-router-dom";
import Heading from "../UI/Heading";
import { requestAdminAPI } from "../../api/posh-api";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import { toTitleCase } from "../../util/format-text";
import LoadingSpinner from "../UI/LoadingSpinner";
import { MdRefresh } from "react-icons/md";
import { UtilityContext } from "../../context/util-context";
import { FaCar } from "react-icons/fa";

// Internal Imports

/** ========================================================================
 * * RegisterVehicle Component
 * This component renders a form to register vehicle with NTTA.
 * ========================================================================== */

export default function RegisterVehicle() {
  const { navigate } = useContext(UtilityContext);
  // Get Vehicle ID from URL
  const [searchParms] = useSearchParams();
  const id = searchParms.get("id");

  const [loading, setLoading] = useState(true);
  const [make, setMake] = useState("Audi");
  const [model, setModel] = useState("100");
  const [vehicle, setVehicle] = useState({});

  /* ====================== GET MAKES AND MODELS ====================== */
  const getVehicle = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "vehicle",
        id: id,
        columns: ["make { name }"],
      });
      setVehicle(response);
      let tempMake = "Audi";
      for (let mk of Object.keys(MakesAndModals)) {
        if (mk.toLowerCase().includes(response.make?.name?.toLowerCase())) {
          tempMake = mk;
          break;
        }
      }
      let tempModel = MakesAndModals[tempMake][0];
      for (let md of MakesAndModals[tempMake]) {
        if (md.toLowerCase().includes(response.model?.toLowerCase())) {
          tempModel = md;
          break;
        }
      }
      setMake(tempMake);
      setModel(tempModel);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== REGISTER VEHICLE ====================== */
  const submitHandler = async () => {
    try {
      const payload = {
        license: document.getElementById("license").value,
        state: document.getElementById("state").value,
        make: make,
        model: model,
        year: document.getElementById("year").value,
        color: document.getElementById("color").value,
        isTempTag: document.getElementById("isTempTag").value === "Yes",
        id: id,
      };
      setLoading(true);
      await requestAdminAPI("register-vehicle", payload);
      navigate(`/vehicles/info?id=${id}`);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== UNREGISTER VEHICLE ====================== */
  const unregisterVehicle = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("unregister-vehicle", { id: id });
      navigate(`/vehicles/info?id=${id}`);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getVehicle();
    else setLoading(false);
  }, [id]);

  if (!id && !loading)
    return (
      <div className="container">
        <p className="text-stone-400">Vehicle ID not provided!</p>
      </div>
    );

  return (
    <div className="container gap-6">
      <Heading
        title="Register Vehicle"
        text="Use this form to register the vehicle with NTTA for tolls. Changing the plate number will deactivate the older plate and create a new toll tag."
      >
        <FaCar
          className="text-xl icon"
          onClick={() => navigate(`/vehicles/info?id=${id}`)}
        />
        <MdRefresh onClick={getVehicle} className="icon" />
      </Heading>
      <div className="gap-4 info">
        <DetailPair
          label="Year"
          edit={true}
          loading={loading}
          inputOptions={{
            id: "year",
            defaultValue: vehicle.year ?? "",
          }}
        />
        <SelectPair
          label="Make"
          edit={true}
          loading={loading}
          options={Object.keys(MakesAndModals)}
          inputOptions={{
            id: "make",
            value: make,
            onChange: (e) => setMake(e.target.value),
          }}
        />
        <SelectPair
          label="Model"
          edit={true}
          loading={loading}
          options={MakesAndModals[make]}
          inputOptions={{
            id: "model",
            value: model,
            onChange: (e) => setModel(e.target.value),
          }}
        />
        <SelectPair
          label="Color"
          edit={true}
          loading={loading}
          options={Colors}
          inputOptions={{
            id: "color",
            defaultValue: toTitleCase(vehicle.color?.toLowerCase()) ?? "Black",
          }}
        />
        <DetailPair
          label="License Plate"
          edit={true}
          loading={loading}
          inputOptions={{
            id: "license",
            defaultValue: vehicle.license,
          }}
        />
        <DetailPair
          label="License State"
          edit={true}
          loading={loading}
          inputOptions={{
            id: "state",
            defaultValue: vehicle.licenseState,
            maxLength: 2,
          }}
        />
        <SelectPair
          label="Is Temp Tag"
          edit={true}
          loading={loading}
          options={["No", "Yes"]}
          inputOptions={{
            id: "isTempTag",
          }}
        />
      </div>

      <div className="info">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <Fragment>
            <div className="btn-primary" onClick={submitHandler}>
              {vehicle.tollTagId ? "Update" : "Confirm"}
            </div>
            {vehicle.tollTagId && (
              <div onClick={unregisterVehicle} className="btn-warning">
                Deactivate Vehicle
              </div>
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
}
